import React, { useState } from "react";
import AdminLayout from "@/components/AdminLayout";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Loader2, Save, ChevronDown } from "lucide-react";

const DAY_LABELS = ["Pon / Mon", "Uto / Tue", "Sri / Wed", "Čet / Thu", "Pet / Fri", "Sub / Sat", "Ned / Sun"];

export default function AdminSettings() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  React.useEffect(() => {
    api.get("/admin/settings").then((r) => {
      setData(r.data);
      setLoading(false);
    });
  }, []);

  const set = (path) => (value) => {
    setData((d) => {
      const next = { ...d };
      const segs = path.split(".");
      let cur = next;
      for (let i = 0; i < segs.length - 1; i++) {
        cur[segs[i]] = { ...(cur[segs[i]] || {}) };
        cur = cur[segs[i]];
      }
      cur[segs[segs.length - 1]] = value;
      return next;
    });
  };

  const updateHourField = (index, field, value) => {
    setData((d) => {
      const hours = [...(d.hours || [])];
      hours[index] = { ...hours[index], [field]: value };
      return { ...d, hours };
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        business_name: data.business_name,
        tagline: data.tagline,
        address: data.address,
        phone: data.phone,
        email: data.email,
        hours: data.hours,
        social: data.social,
        default_locale: data.default_locale,
        contact_form_recipient: data.contact_form_recipient,
        coordinates: data.coordinates,
      };
      const { data: fresh } = await api.put("/admin/settings", payload);
      setData(fresh);
      toast.success("Settings saved");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  if (loading || !data) {
    return (
      <AdminLayout>
        <div className="py-16 flex justify-center text-muted-foreground">
          <Loader2 className="w-5 h-5 animate-spin" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-10" data-testid="admin-settings-page">
        <div className="flex items-end justify-between gap-4 flex-wrap">
          <div>
            <p className="text-xs uppercase tracking-[0.3em] text-primary/80">CMS</p>
            <h1 className="font-display text-4xl sm:text-5xl mt-2">Site settings</h1>
          </div>
          <Button
            onClick={save}
            disabled={saving}
            className="bg-primary text-primary-foreground hover:bg-primary/90"
            data-testid="settings-save-button"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : (<><Save className="w-4 h-4 mr-2" /> Save</>)}
          </Button>
        </div>

        <section className="border border-border rounded-xl bg-card p-6 space-y-5" data-testid="settings-general-card">
          <h2 className="font-display text-xl">General</h2>
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Business name</Label>
              <Input value={data.business_name || ""} onChange={(e) => set("business_name")(e.target.value)} data-testid="settings-business-name" />
            </div>
            <div className="space-y-2">
              <Label>Default locale</Label>
              <Select value={data.default_locale || "hr"} onValueChange={set("default_locale")}>
                <SelectTrigger data-testid="settings-default-locale"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="hr">Hrvatski</SelectItem>
                  <SelectItem value="en">English</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Tagline (HR)</Label>
              <Input value={data.tagline?.hr || ""} onChange={(e) => set("tagline.hr")(e.target.value)} data-testid="settings-tagline-hr" />
            </div>
            <div className="space-y-2">
              <Label>Tagline (EN)</Label>
              <Input value={data.tagline?.en || ""} onChange={(e) => set("tagline.en")(e.target.value)} data-testid="settings-tagline-en" />
            </div>
            <div className="space-y-2 sm:col-span-2">
              <Label>Address (HR)</Label>
              <Input value={data.address?.hr || ""} onChange={(e) => set("address.hr")(e.target.value)} data-testid="settings-address-hr" />
            </div>
            <div className="space-y-2 sm:col-span-2">
              <Label>Address (EN)</Label>
              <Input value={data.address?.en || ""} onChange={(e) => set("address.en")(e.target.value)} data-testid="settings-address-en" />
            </div>
            <div className="space-y-2">
              <Label>Phone</Label>
              <Input value={data.phone || ""} onChange={(e) => set("phone")(e.target.value)} data-testid="settings-phone" />
            </div>
            <div className="space-y-2">
              <Label>Public email</Label>
              <Input type="email" value={data.email || ""} onChange={(e) => set("email")(e.target.value)} data-testid="settings-email" />
            </div>
            <div className="space-y-2">
              <Label>Contact form recipient</Label>
              <Input value={data.contact_form_recipient || ""} onChange={(e) => set("contact_form_recipient")(e.target.value)} data-testid="settings-contact-recipient" />
            </div>
          </div>
        </section>

        <section className="border border-border rounded-xl bg-card p-6 space-y-5" data-testid="settings-hours-card">
          <h2 className="font-display text-xl">Hours</h2>
          <div className="space-y-2">
            {(data.hours || []).map((h, idx) => (
              <div key={idx} className="grid grid-cols-[110px_1fr_1fr_100px] gap-3 items-center" data-testid={`hours-row-${idx}`}>
                <div className="text-sm text-muted-foreground">{DAY_LABELS[h.day]}</div>
                <Input
                  type="time"
                  value={h.open || ""}
                  disabled={h.closed}
                  onChange={(e) => updateHourField(idx, "open", e.target.value)}
                  data-testid={`hours-open-${idx}`}
                />
                <Input
                  type="time"
                  value={h.close || ""}
                  disabled={h.closed}
                  onChange={(e) => updateHourField(idx, "close", e.target.value)}
                  data-testid={`hours-close-${idx}`}
                />
                <div className="flex items-center gap-2">
                  <Checkbox
                    id={`closed-${idx}`}
                    checked={!!h.closed}
                    onCheckedChange={(v) => updateHourField(idx, "closed", Boolean(v))}
                    data-testid={`hours-closed-${idx}`}
                  />
                  <Label htmlFor={`closed-${idx}`} className="text-xs text-muted-foreground">Closed</Label>
                </div>
              </div>
            ))}
          </div>
        </section>

        <section className="border border-border rounded-xl bg-card p-6 space-y-5" data-testid="settings-social-card">
          <h2 className="font-display text-xl">Social & Map</h2>
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Instagram URL</Label>
              <Input value={data.social?.instagram || ""} onChange={(e) => set("social.instagram")(e.target.value)} data-testid="settings-instagram" />
            </div>
            <div className="space-y-2">
              <Label>Facebook URL</Label>
              <Input value={data.social?.facebook || ""} onChange={(e) => set("social.facebook")(e.target.value)} data-testid="settings-facebook" />
            </div>
            <div className="space-y-2">
              <Label>Latitude</Label>
              <Input type="number" step="0.0001" value={data.coordinates?.lat ?? ""} onChange={(e) => set("coordinates.lat")(parseFloat(e.target.value))} data-testid="settings-coord-lat" />
            </div>
            <div className="space-y-2">
              <Label>Longitude</Label>
              <Input type="number" step="0.0001" value={data.coordinates?.lng ?? ""} onChange={(e) => set("coordinates.lng")(parseFloat(e.target.value))} data-testid="settings-coord-lng" />
            </div>
          </div>
        </section>
      </div>
    </AdminLayout>
  );
}
